<template>
  <ContentWrapperControl
    no_bottom
    full_width
    :centered="false"
    is_grid
    class="h-screen bg-primary overflow-hidden flex flex-col"
  >
    <!-- Header -->
    <div
      class="flex flex-row mt-2 justify-between items-center w-full text-white text-sm font-medium p-4"
    >
      <!-- Left button -->
      <button
        class="rounded-lg p-1 flex items-center justify-center shadow-md navigation"
      >
        <img
          src="@/assets/icons/papsi_arrow_back_alt.svg"
          alt="Back"
          class="w-5 h-5"
        />
      </button>

      <!-- Center logo (small) -->
      <img
        :src="getImage('ga/', 'papsi_logo.png')"
        alt="PAPSI Logo"
        class="h-6 w-auto object-contain"
      />

      <!-- Right button -->
      <button
        class="rounded-lg p-1 flex items-center justify-center shadow-md navigation"
      >
        <img
          src="@/assets/icons/papsi_ballot.svg"
          alt="Summary"
          class="w-5 h-5"
        />
      </button>
    </div>

    <span class="font-bold text-secondary text-xl mt-3 mb-3 text-center block">
      {{ this.lang("lbl_0000169") }}
    </span>

    <!-- Status chips -->
    <div class="status-chips">
      <div
        class="chip"
        :style="{ opacity: getSelectedCandidates.officers === 0 ? 0.5 : 1 }"
      >
        {{ getSelectedCandidates.officers }}/3 - {{ this.lang("lbl_0000174") }}
      </div>

      <div
        class="chip"
        :style="{ opacity: getSelectedCandidates.enlisted === 0 ? 0.5 : 1 }"
      >
        {{ getSelectedCandidates.enlisted }}/3 - {{ this.lang("lbl_0000175") }}
      </div>

      <div
        class="chip"
        :style="{ opacity: getSelectedCandidates.civilians === 0 ? 0.5 : 1 }"
      >
        {{ getSelectedCandidates.civilians }}/2 - {{ this.lang("lbl_0000350") }}
      </div>
    </div>

    <div
      class="bg-white rounded-t-3xl w-full p-4 flex-grow overflow-y-auto min-h-0 rounded-t-lg"
    >
      <div
        style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px"
      >
        <div
          v-for="candidate in election.candidates.bod"
          :key="candidate.code"
          @click="toggleSelect('bod', candidate.code)"
          :class="{ selected: isSelected('bod', candidate.code) }"
          class="candidate-card col-span-1 relative border border-green-600 rounded-xl shadow-sm flex flex-col items-center justify-center bg-white w-full"
        >
          <img
            v-if="isSelected('bod', candidate.code)"
            src="@/assets/icons/papsi_check_circle_filled.svg"
            class="check-icon"
            alt="Selected"
          />
          <img
            :src="getImage('ga/', 'papsi_placeholder.jpg')"
            class="rounded-lg"
            alt="Candidate Photo"
          />
          <span
            class="font-bold uppercase text-center text-sm leading-tight h-10 flex items-center justify-center"
          >
            {{ candidate.name }}
          </span>
          <span class="text-[10px] text-xs mt-1 text-center">
            {{ candidate.type }}
          </span>
        </div>
      </div>
    </div>

    <div class="bottom-bar">
      <!-- Progress Bar -->
      <div class="progress-container">
        <div
          class="progress-fill"
          :style="{ width: getProgressPercent + '%' }"
        ></div>
      </div>

      <div class="bottom-inner">
        <!-- Counter -->
        <div class="votes-text">
          <div class="label mb-1">Selected Votes</div>
          <div class="count">{{ getTotalSelected }} of {{ getTotalLimit }}</div>
        </div>
        <div>
          <!-- Clear Button -->
          <ButtonControl
            :outline="true"
            :full_width="false"
            class="w-28 mr-3"
            @click="election.selected.bod = new Set()"
          >
            {{ this.lang("lbl_0000017") }}
          </ButtonControl>

          <!-- Proceed Button -->
          <ButtonControl
            :full_width="false"
            class="w-28"
            :disabled="getTotalSelected < 8"
          >
            {{ this.lang("lbl_0000015") }}
          </ButtonControl>
        </div>
      </div>
    </div>
  </ContentWrapperControl>
</template>

<script>
import mixin from "@/mixin";
import ContentWrapperControl from "@/components/controls/ContentWrapperControl.vue";
import GA from "@/services/GA";
import ButtonControl from "@/components/controls/ButtonControl.vue";

export default {
  mixins: [mixin],
  emits: ["checkauth", "hidenavbar", "nofooter", "showloader", "hideloader"],
  components: {
    ContentWrapperControl,
    ButtonControl,
  },
  data() {
    return {
      state: null,
      placeholder: "JUAN DELA CRUZ",
      election: {
        code: null,
        title: null,
        date: null,
        candidates: {
          bod: this.$store.state.ga.candidates.bod,
          voting_bod: true,
          elecom: this.$store.state.ga.candidates.elecom,
          voting_elecom: false,
          audit: this.$store.state.ga.candidates.audit,
          voting_audit: false,
        },
        selected: {
          bod: new Set(),
          elecom: new Set(),
          audit: new Set(),
        },
      },
    };
  },
  mounted() {
    this.$emit("checkauth");
    this.$emit("hidenavbar");
    this.$emit("nofooter", true);
    if (
      this.$store.state.ga.code === null ||
      this.$store.state.ga.candidates.bod.length === 0
    ) {
      this.loadElection();
    }
  },
  computed: {
    getSelectedCandidates() {
      const bod_candidates = this.election.candidates.bod;

      const counts = {
        officers: 0,
        enlisted: 0,
        civilians: 0,
      };

      bod_candidates.forEach((candidate) => {
        if (this.election.selected.bod.has(candidate.code)) {
          const type = candidate.type?.toLowerCase();

          if (type.includes("officer")) counts.officers++;
          else if (type.includes("enlisted")) counts.enlisted++;
          else if (type.includes("civilian")) counts.civilians++;
        }
      });

      return counts;
    },

    getSelectedLimits() {
      return {
        officers: 3,
        enlisted: 3,
        civilians: 2,
      };
    },

    getTotalLimit() {
      return (
        this.getSelectedLimits.officers +
        this.getSelectedLimits.enlisted +
        this.getSelectedLimits.civilians
      );
    },

    getTotalSelected() {
      const c = this.getSelectedCandidates;
      return c.officers + c.enlisted + c.civilians;
    },

    getProgressPercent() {
      if (!this.getTotalLimit) return 0;
      return (this.getTotalSelected / this.getTotalLimit) * 100;
    },
  },
  methods: {
    toggleSelect(group, code) {
      const set = this.election.selected[group];
      if (!set) return;

      // find candidate
      const candidate = this.election.candidates[group].find(
        (c) => c.code === code,
      );
      if (!candidate) return;

      const type = candidate.type?.toLowerCase();

      // deselect is always allowed
      if (set.has(code)) {
        set.delete(code);
        this.election.selected[group] = new Set(set);
        return;
      }

      // enforce limits
      if (type.includes("officer") && this.getSelectedCandidates.officers >= 3)
        return;
      if (type.includes("enlisted") && this.getSelectedCandidates.enlisted >= 3)
        return;
      if (
        type.includes("civilian") &&
        this.getSelectedCandidates.civilians >= 2
      )
        return;

      // select
      set.add(code);
      this.election.selected[group] = new Set(set);
    },

    isSelected(group, code) {
      return this.election.selected[group]?.has(code);
    },
    getImage(path, file_name) {
      // const path = "ga/";
      // const file_name = "papsi_placeholder.jpg";
      if (process.env.VUE_APP_CDN_URL) {
        return process.env.VUE_APP_CDN_URL + path + file_name;
      }
    },
    loadElection() {
      this.$emit("showloader");
      GA.getCurrentElection()
        .then((response) => {
          if (response.status === 200) {
            const { code, title, date } = response.data.data;

            this.$store.state.ga.code = code;
            this.election.code = code;
            this.$store.state.ga.title = title;
            this.election.title = title;
            this.$store.state.ga.date = date;
            this.election.date = date;
            this.loadCandidates();
          }
        })
        .catch((error) => {
          this.$emit("error", error);
        });
    },
    loadCandidates() {
      this.$emit("showloader");
      GA.getAllCandidates(this.election.code, this.$store.state.info.hash)
        .then((response) => {
          if (response.status === 200) {
            this.election.candidates.bod = response.data.data.bod;
            this.$store.state.ga.candidates.bod = response.data.data.bod;
            this.election.candidates.elecom = response.data.data.elecom;
            this.$store.state.ga.candidates.elecom = response.data.data.elecom;
            this.election.candidates.audit = response.data.data.audit;
            this.$store.state.ga.candidates.audit = response.data.data.audit;
          }
        })
        .catch((error) => {
          this.$emit("error", error);
        })
        .finally(() => this.$emit("hideloader"));
    },
  },
};
</script>
<style scoped>
.candidate-card {
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  transition: 0.2s;
  border-color: #d7dbdd;
}

.candidate-card.selected {
  border-color: #016b00;
  background: #eaffe9;
}

.status-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.chip {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.navigation {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #f3f4f6;
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.bottom-inner {
  padding: 10px 16px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
}

.votes-text {
  font-size: 11px;
  line-height: 1.1;
}

.votes-text .label {
  color: #6b7280;
}

.votes-text .count {
  color: #016b00;
  font-weight: 700;
  font-size: 15px;
}

.progress-container {
  width: 100%;
  height: 6px;
  background: rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #016b00;
  transition: width 0.25s ease;
}

.check-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  border-radius: 999px;
}
</style>
